import React, { useEffect, useState, useCallback } from 'react';
import { useLegislation } from '../../hooks/useLegislation';
import { LegislationLink, LegislationCategory } from '../../types/Legislation';
import s from '../../styles/AdminLegislation.module.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

const EMPTY_LINK = {
  linkLabel: '', linkUrl: '', bodyText: '',
  isActive: true, categoryId: '', subcategoryId: '',
};

// ── Main component ────────────────────────────────────────────────────────────

const AdminLegislation: React.FC = () => {
  const {
    fetchAdminLinks, fetchAdminCategories,
    createLink, updateLink, deleteLink,
    createCategory, updateCategory, deleteCategory,
    createSubcategory, updateSubcategory, deleteSubcategory,
  } = useLegislation();

  const [links, setLinks] = useState<LegislationLink[]>([]);
  const [categories, setCategories] = useState<LegislationCategory[]>([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [loadingCats, setLoadingCats] = useState(false);

  // Link form state
  const [linkForm, setLinkForm] = useState(EMPTY_LINK);
  const [editingLinkId, setEditingLinkId] = useState<number | null>(null);
  const [linkError, setLinkError] = useState('');
  const [linkSaving, setLinkSaving] = useState(false);

  // Category form state
  const [newCatName, setNewCatName] = useState('');
  const [editingCat, setEditingCat] = useState<{ id: string; name: string } | null>(null);

  // Subcategory form state
  const [newSubName, setNewSubName] = useState('');
  const [newSubCatId, setNewSubCatId] = useState('');
  const [editingSub, setEditingSub] = useState<{ id: string; name: string; categoryId: string } | null>(null);

  const reload = useCallback(async () => {
    setLoadingLinks(true);
    setLoadingCats(true);
    try {
      const [l, c] = await Promise.all([fetchAdminLinks(), fetchAdminCategories()]);
      setLinks(l);
      setCategories(c);
    } finally {
      setLoadingLinks(false);
      setLoadingCats(false);
    }
  }, [fetchAdminLinks, fetchAdminCategories]);

  useEffect(() => { reload(); }, [reload]);

  // Derive available subcategories based on selected category in the link form
  const subcatsForForm = (categories.find(c => c.id === linkForm.categoryId)?.subcategories) ?? [];

  // ── Link handlers ───────────────────────────────────────────────────────────

  const handleEditLink = (link: LegislationLink) => {
    setEditingLinkId(link.id);
    setLinkForm({
      linkLabel: link.linkLabel,
      linkUrl: link.linkUrl,
      bodyText: link.bodyText ?? '',
      isActive: link.isActive,
      categoryId: link.categoryId ?? '',
      subcategoryId: link.subcategoryId ?? '',
    });
    setLinkError('');
  };

  const handleCancelLink = () => {
    setEditingLinkId(null);
    setLinkForm(EMPTY_LINK);
    setLinkError('');
  };

  const handleSaveLink = async () => {
    if (!linkForm.linkLabel.trim() || !linkForm.linkUrl.trim()) {
      setLinkError('Label and URL are required.');
      return;
    }
    setLinkSaving(true);
    setLinkError('');
    try {
      const payload = {
        linkLabel: linkForm.linkLabel.trim(),
        linkUrl: linkForm.linkUrl.trim(),
        bodyText: linkForm.bodyText.trim() || undefined,
        isActive: linkForm.isActive,
        categoryId: linkForm.categoryId || undefined,
        subcategoryId: linkForm.subcategoryId || undefined,
      };
      if (editingLinkId !== null) {
        await updateLink(editingLinkId, payload);
      } else {
        await createLink(payload as any);
      }
      handleCancelLink();
      await reload();
    } catch {
      setLinkError('Failed to save link. Please try again.');
    } finally {
      setLinkSaving(false);
    }
  };

  const handleDeleteLink = async (id: number) => {
    if (!window.confirm('Delete this link?')) return;
    await deleteLink(id);
    await reload();
  };

  const handleToggleActive = async (link: LegislationLink) => {
    await updateLink(link.id, { isActive: !link.isActive });
    await reload();
  };

  // ── Category handlers ───────────────────────────────────────────────────────

  const handleSaveCat = async () => {
    const name = (editingCat ? editingCat.name : newCatName).trim();
    if (!name) return;
    if (editingCat) {
      await updateCategory(editingCat.id, name);
      setEditingCat(null);
    } else {
      await createCategory(name);
      setNewCatName('');
    }
    await reload();
  };

  const handleDeleteCat = async (id: string) => {
    if (!window.confirm('Delete this category? Links assigned to it will lose their category.')) return;
    await deleteCategory(id);
    await reload();
  };

  // ── Subcategory handlers ────────────────────────────────────────────────────

  const handleSaveSub = async () => {
    const name = (editingSub ? editingSub.name : newSubName).trim();
    const catId = editingSub ? editingSub.categoryId : newSubCatId;
    if (!name || !catId) return;
    if (editingSub) {
      await updateSubcategory(editingSub.id, name);
      setEditingSub(null);
    } else {
      await createSubcategory(name, catId);
      setNewSubName('');
      setNewSubCatId('');
    }
    await reload();
  };

  const handleDeleteSub = async (id: string) => {
    if (!window.confirm('Delete this subcategory?')) return;
    await deleteSubcategory(id);
    await reload();
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className={s.page}>
      <h2 className={s.pageTitle}>⚖️ Legislation Management</h2>

      {/* ── Categories & Subcategories ─────────────────────────────────────── */}
      <div className={s.twoCol}>
        {/* Categories */}
        <section className={s.panel}>
          <h3 className={s.panelTitle}>Categories</h3>
          {loadingCats ? <p className={s.loading}>Loading…</p> : (
            <table className={s.table}>
              <thead>
                <tr><th>Name</th><th></th></tr>
              </thead>
              <tbody>
                {categories.map(cat => (
                  <tr key={cat.id}>
                    <td>
                      {editingCat?.id === cat.id
                        ? <input className={s.input} value={editingCat.name}
                            onChange={e => setEditingCat({ ...editingCat, name: e.target.value })} />
                        : cat.category_name}
                    </td>
                    <td className={s.actions}>
                      {editingCat?.id === cat.id ? (
                        <>
                          <button className={s.btnSave} onClick={handleSaveCat}>Save</button>
                          <button className={s.btnCancel} onClick={() => setEditingCat(null)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button className={s.btnEdit} onClick={() => setEditingCat({ id: cat.id, name: cat.category_name })}>Edit</button>
                          <button className={s.btnDelete} onClick={() => handleDeleteCat(cat.id)}>Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td>
                    <input className={s.input} placeholder="New category name"
                      value={newCatName} onChange={e => setNewCatName(e.target.value)} />
                  </td>
                  <td>
                    <button className={s.btnAdd} onClick={handleSaveCat} disabled={!newCatName.trim()}>Add</button>
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </section>

        {/* Subcategories */}
        <section className={s.panel}>
          <h3 className={s.panelTitle}>Subcategories</h3>
          {loadingCats ? <p className={s.loading}>Loading…</p> : (
            <table className={s.table}>
              <thead>
                <tr><th>Name</th><th>Category</th><th></th></tr>
              </thead>
              <tbody>
                {categories.flatMap(cat =>
                  (cat.subcategories ?? []).map(sub => (
                    <tr key={sub.id}>
                      <td>
                        {editingSub?.id === sub.id
                          ? <input className={s.input} value={editingSub.name}
                              onChange={e => setEditingSub({ ...editingSub, name: e.target.value })} />
                          : sub.subcategory_name}
                      </td>
                      <td>{cat.category_name}</td>
                      <td className={s.actions}>
                        {editingSub?.id === sub.id ? (
                          <>
                            <button className={s.btnSave} onClick={handleSaveSub}>Save</button>
                            <button className={s.btnCancel} onClick={() => setEditingSub(null)}>Cancel</button>
                          </>
                        ) : (
                          <>
                            <button className={s.btnEdit} onClick={() => setEditingSub({ id: sub.id, name: sub.subcategory_name, categoryId: cat.id })}>Edit</button>
                            <button className={s.btnDelete} onClick={() => handleDeleteSub(sub.id)}>Delete</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
                <tr>
                  <td>
                    <input className={s.input} placeholder="New subcategory name"
                      value={newSubName} onChange={e => setNewSubName(e.target.value)} />
                  </td>
                  <td>
                    <select className={s.select} value={newSubCatId} onChange={e => setNewSubCatId(e.target.value)}>
                      <option value="">Select category</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.category_name}</option>)}
                    </select>
                  </td>
                  <td>
                    <button className={s.btnAdd} onClick={handleSaveSub} disabled={!newSubName.trim() || !newSubCatId}>Add</button>
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </section>
      </div>

      {/* ── Link Form ──────────────────────────────────────────────────────── */}
      <section className={s.panel}>
        <h3 className={s.panelTitle}>{editingLinkId !== null ? 'Edit Link' : 'Add Link'}</h3>
        <div className={s.formGrid}>
          <label className={s.formLabel}>
            Label *
            <input className={s.input} placeholder="Display text" value={linkForm.linkLabel}
              onChange={e => setLinkForm(f => ({ ...f, linkLabel: e.target.value }))} />
          </label>
          <label className={s.formLabel}>
            URL *
            <input className={s.input} placeholder="https://..." value={linkForm.linkUrl}
              onChange={e => setLinkForm(f => ({ ...f, linkUrl: e.target.value }))} />
          </label>
          <label className={s.formLabel}>
            Category
            <select className={s.select} value={linkForm.categoryId}
              onChange={e => setLinkForm(f => ({ ...f, categoryId: e.target.value, subcategoryId: '' }))}>
              <option value="">None</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.category_name}</option>)}
            </select>
          </label>
          <label className={s.formLabel}>
            Subcategory
            <select className={s.select} value={linkForm.subcategoryId}
              onChange={e => setLinkForm(f => ({ ...f, subcategoryId: e.target.value }))}
              disabled={!linkForm.categoryId}>
              <option value="">None</option>
              {subcatsForForm.map(sub => <option key={sub.id} value={sub.id}>{sub.subcategory_name}</option>)}
            </select>
          </label>
          <label className={`${s.formLabel} ${s.fullWidth}`}>
            Body Text (optional description shown above the link)
            <textarea className={s.textarea} rows={2} value={linkForm.bodyText}
              onChange={e => setLinkForm(f => ({ ...f, bodyText: e.target.value }))} />
          </label>
          <label className={s.checkboxLabel}>
            <input type="checkbox" checked={linkForm.isActive}
              onChange={e => setLinkForm(f => ({ ...f, isActive: e.target.checked }))} />
            Active (visible to users)
          </label>
        </div>
        {linkError && <p className={s.error}>{linkError}</p>}
        <div className={s.formActions}>
          <button className={s.btnSave} onClick={handleSaveLink} disabled={linkSaving}>
            {linkSaving ? 'Saving…' : editingLinkId !== null ? 'Update Link' : 'Add Link'}
          </button>
          {editingLinkId !== null && (
            <button className={s.btnCancel} onClick={handleCancelLink}>Cancel</button>
          )}
        </div>
      </section>

      {/* ── Links Table ────────────────────────────────────────────────────── */}
      <section className={s.panel}>
        <h3 className={s.panelTitle}>All Links ({links.length})</h3>
        {loadingLinks ? <p className={s.loading}>Loading…</p> : (
          <div className={s.tableWrapper}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Category</th>
                  <th>Subcategory</th>
                  <th>Body Text</th>
                  <th>Active</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {links.map(link => (
                  <tr key={link.id}>
                    <td>
                      <a href={link.linkUrl} target="_blank" rel="noopener noreferrer">{link.linkLabel}</a>
                    </td>
                    <td>{link.categoryName ?? '—'}</td>
                    <td>{link.subcategoryName ?? '—'}</td>
                    <td className={s.truncate}>{link.bodyText ?? '—'}</td>
                    <td>
                      <button
                        className={link.isActive ? s.badgeActive : s.badgeInactive}
                        onClick={() => handleToggleActive(link)}
                        title="Toggle active"
                      >
                        {link.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className={s.actions}>
                      <button className={s.btnEdit} onClick={() => handleEditLink(link)}>Edit</button>
                      <button className={s.btnDelete} onClick={() => handleDeleteLink(link.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
                {links.length === 0 && (
                  <tr><td colSpan={6} className={s.empty}>No links yet. Add one above.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminLegislation;
